import { io, Socket } from 'socket.io-client';
import { envConfig } from '../config/environment';

export type LiveStreamMode = 'THUMBNAIL' | 'DETAIL';

export interface LiveScreenFramePayload {
  deviceId?: string;
  monitorId?: string;
  sequence?: number;
  capturedAt?: string;
  mode?: string;
  mimeType?: string;
  width?: number;
  height?: number;
  dataBase64?: string;
}

export interface LiveScreenStatePayload {
  deviceId?: string;
  streamAvailable?: boolean;
  captureRunning?: boolean;
  captureAllowed?: boolean;
  capturePolicyReason?: string | null;
  reason?: string | null;
  reasonText?: string;
  streamMode?: string;
  lastError?: string | null;
  [key: string]: unknown;
}

export type LiveActivityEventPayload = {
  event_id?: string;
  device_uid?: string | null;
  device_id?: string | null;
  employee_id?: string | null;
  employee_name?: string | null;
  tenant_id?: string;
  event_type?: string;
  description?: string;
  context?: string | null;
  application_name?: string | null;
  domain?: string | null;
  monitor_id?: string | null;
  snapshot_id?: string | null;
  campaign_id?: string | null;
  campaign?: string | null;
  started_at?: string;
  source?: string | null;
};

type FrameHandler = (frame: LiveScreenFramePayload) => void;
type StateHandler = (state: LiveScreenStatePayload) => void;
type ErrorHandler = (error: { message?: string }) => void;
type ActivityHandler = (event: LiveActivityEventPayload) => void;

class LiveScreenSocketService {
  private socket: Socket | null = null;
  private frameHandlers = new Set<FrameHandler>();
  private stateHandlers = new Set<StateHandler>();
  private errorHandlers = new Set<ErrorHandler>();
  private subscriptions = new Map<string, { deviceId: string; mode: LiveStreamMode; subscriptionId: string }>();
  private activitySubscriptions = new Map<string, { deviceId: string; subscriptionId: string }>();
  private activityHandlers = new Set<ActivityHandler>();
  private connectPromise: Promise<void> | null = null;

  private getServerUrl(): string {
    return envConfig.apiUrl.startsWith('/')
      ? window.location.origin
      : envConfig.apiUrl.replace(/\/api$/, '');
  }

  connect(): Promise<void> {
    if (this.socket?.connected) return Promise.resolve();
    if (this.connectPromise) return this.connectPromise;

    this.connectPromise = new Promise<void>((resolve, reject) => {
      const token = localStorage.getItem('token');
      if (!token) {
        this.connectPromise = null;
        reject(new Error('Authentication required'));
        return;
      }

      if (this.socket) {
        this.socket.removeAllListeners();
        this.socket.disconnect();
        this.socket = null;
      }

      this.socket = io(`${this.getServerUrl()}/live-screen`, {
        transports: ['websocket', 'polling'],
        autoConnect: true,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 8,
        auth: { token },
      });

      const onConnect = () => {
        cleanup();
        resolve();
      };
      const onError = (err: Error) => {
        cleanup();
        this.connectPromise = null;
        reject(err);
      };
      const cleanup = () => {
        this.socket?.off('connect', onConnect);
        this.socket?.off('connect_error', onError);
      };

      this.socket.on('connect', onConnect);
      this.socket.on('connect_error', onError);

      this.socket.on('live_screen_frame', (frame: LiveScreenFramePayload) => {
        this.frameHandlers.forEach((handler) => handler(frame));
      });
      this.socket.on('live_screen_state', (state: LiveScreenStatePayload) => {
        this.stateHandlers.forEach((handler) => handler(state));
      });
      this.socket.on('live_screen_error', (error: { message?: string }) => {
        this.errorHandlers.forEach((handler) => handler(error));
      });
      this.socket.on('live_activity_event', (event: LiveActivityEventPayload) => {
        this.activityHandlers.forEach((handler) => handler(event));
      });

      this.socket.on('disconnect', () => {
        this.subscriptions.clear();
        this.activitySubscriptions.clear();
      });
    });

    return this.connectPromise.finally(() => {
      this.connectPromise = null;
    });
  }

  async subscribe(
    deviceId: string,
    mode: LiveStreamMode,
  ): Promise<{ subscriptionId: string; correlationId?: string; mode: LiveStreamMode } | null> {
    await this.connect();
    if (!this.socket?.connected) throw new Error('Live Screen socket not connected');

    const existing = this.subscriptions.get(deviceId);
    if (existing && existing.mode === mode) {
      return { subscriptionId: existing.subscriptionId, mode };
    }
    if (existing) {
      await this.unsubscribe(deviceId);
    }

    return new Promise((resolve, reject) => {
      this.socket!.emit('subscribe', { deviceId, mode }, (ack: any) => {
        if (!ack?.success) {
          reject(new Error(ack?.message || 'Subscribe failed'));
          return;
        }
        const subscriptionId = String(ack.data?.subscriptionId || '');
        this.subscriptions.set(deviceId, { deviceId, mode, subscriptionId });
        resolve({
          subscriptionId,
          correlationId: ack.data?.correlationId,
          mode: (ack.data?.mode as LiveStreamMode) || mode,
        });
      });
    });
  }

  async unsubscribe(deviceId: string): Promise<void> {
    const sub = this.subscriptions.get(deviceId);
    if (!sub || !this.socket) return;
    this.subscriptions.delete(deviceId);
    await new Promise<void>((resolve) => {
      this.socket!.emit('unsubscribe', { subscriptionId: sub.subscriptionId }, () => resolve());
      setTimeout(() => resolve(), 1500);
    });
  }

  heartbeat(deviceId: string): void {
    const sub = this.subscriptions.get(deviceId);
    if (!sub || !this.socket?.connected) return;
    this.socket.emit('heartbeat_subscription', { subscriptionId: sub.subscriptionId });
  }

  async subscribeActivity(deviceId: string): Promise<{ subscriptionId: string } | null> {
    await this.connect();
    if (!this.socket?.connected) throw new Error('Live Screen socket not connected');

    const existing = this.activitySubscriptions.get(deviceId);
    if (existing) {
      return { subscriptionId: existing.subscriptionId };
    }

    return new Promise((resolve, reject) => {
      this.socket!.emit('subscribe_activity', { deviceId }, (ack: any) => {
        if (!ack?.success) {
          reject(new Error(ack?.message || 'Activity subscribe failed'));
          return;
        }
        const subscriptionId = String(ack.data?.subscriptionId || '');
        this.activitySubscriptions.set(deviceId, { deviceId, subscriptionId });
        resolve({ subscriptionId });
      });
    });
  }

  async unsubscribeActivity(deviceId: string): Promise<void> {
    const sub = this.activitySubscriptions.get(deviceId);
    if (!sub || !this.socket) return;
    this.activitySubscriptions.delete(deviceId);
    await new Promise<void>((resolve) => {
      this.socket!.emit('unsubscribe_activity', { subscriptionId: sub.subscriptionId }, () => resolve());
      setTimeout(() => resolve(), 1500);
    });
  }

  onLiveActivity(handler: ActivityHandler): () => void {
    this.activityHandlers.add(handler);
    return () => this.activityHandlers.delete(handler);
  }

  onFrame(handler: FrameHandler): () => void {
    this.frameHandlers.add(handler);
    return () => this.frameHandlers.delete(handler);
  }

  onState(handler: StateHandler): () => void {
    this.stateHandlers.add(handler);
    return () => this.stateHandlers.delete(handler);
  }

  onError(handler: ErrorHandler): () => void {
    this.errorHandlers.add(handler);
    return () => this.errorHandlers.delete(handler);
  }

  getSubscriptionId(deviceId: string): string | null {
    return this.subscriptions.get(deviceId)?.subscriptionId || null;
  }

  disconnect(): void {
    this.subscriptions.clear();
    this.activitySubscriptions.clear();
    this.frameHandlers.clear();
    this.stateHandlers.clear();
    this.errorHandlers.clear();
    this.activityHandlers.clear();
    this.connectPromise = null;
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
  }
}

const liveScreenSocket = new LiveScreenSocketService();
export default liveScreenSocket;
